import React, { useEffect, useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';

import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import useLayout from 'components/Layout/useLayout';
import TemplateService from 'api/TemplateService';
import Contents from 'components/Layout/Contents';
import Spinner from 'components/Spinner/Spinner';
import Table from 'components/Table/Table';
import TierService from 'api/TierService';
import { TableContext } from '../../components/Table/TableContext';
import TierRowItem from './TierTable/TierRowItem';

export default function Tiers() {
  const queryClient = useQueryClient();
  const { scrollToTop } = useLayout();
  const { data: contentQueryData, isLoading: isContentQueryLoading } = useQuery(
    'contentBlock-options',
    TemplateService.getAllContentBlocksOptions
  );
  const { data, isLoading, isError, error, isRefetching } = useQuery(
    'tiers',
    TierService.getAll,
    {
      staleTime: 0,
    }
  );
  const [isDeleting, setDeleting] = useState(false);
  const [open, setOpen] = useState(false);
  const [tierParam, setTierParam] = useState('');
  const [errMsg, setErrMsg] = useState('');

  const mutation = useMutation((tierId) => TierService.deleteById(tierId), {
    onMutate: () => {
      setDeleting(true);
    },
    onSuccess: () => {
      setErrMsg();
      setDeleting(false);
      queryClient.invalidateQueries('tiers');
    },
    onError: (err) => {
      setErrMsg(err.response.data.title);
    },
  });

  const navigate = useNavigate();

  const handleClickEdit = (tierId) => navigate(`/tiers/${tierId}/edit`);

  const handleClickDelete = (tierId) => {
    setOpen(true);
    setTierParam(tierId);
  };

  const onDeleteConfirm = () => {
    mutation.mutate(tierParam);
    // console.log(tierParam);
    setOpen(false);
  };

  const formatContentBlocksLabel = (blocks) => {
    const temp = [];
    blocks.map((b) => {
      const f = contentQueryData?.find((d) => d.value === b);
      if (f) {
        temp.push(f.label);
      }
      return b;
    });
    return temp.join(', ');
  };

  const actions = useMemo(
    () => ({
      onClickEdit: handleClickEdit,
      onClickDelete: handleClickDelete,
      formatContentBlocksLabel,
    }),
    []
  );

  useEffect(() => {
    scrollToTop();
  }, []);

  if (isLoading || isContentQueryLoading.isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return (
      <>
        <h2>There is an error</h2>
        <p className="alert alert-danger">{error.message}</p>
      </>
    );
  }

  const renderError = () => (
    <p className="alert alert-danger">
      <FontAwesomeIcon icon={faExclamationCircle} /> {errMsg}
    </p>
  );

  return (
    <Contents>
      <TableContext.Provider value={actions}>
        <Table
          title="Partner Tiers"
          name="tier"
          columns={[
            'Name',
            'Title',
            'Description',
            'Allowed Templates',
            'Allowed Content Blocks',
          ]}
          dataSource={data.data}
          isLoading={
            isContentQueryLoading.isLoading ||
            isLoading ||
            isDeleting ||
            isRefetching
          }
          isError={isError}
          creationLink="/tiers/add"
          rowComponent={TierRowItem}
          actionWidth="110px"
          striped
          bordered
          hover
          renderError={renderError}
          enablePagination
        />
      </TableContext.Provider>
      <ModalDialog
        open={open}
        setOpen={setOpen}
        header="Delete Tier"
        message="You are about to delete a tier. Are you sure about it?"
        onConfirm={onDeleteConfirm}
        dataTestid="Modal-delete"
      />
    </Contents>
  );
}
